import cardService from '@/services/card.service'
import CookieService from '@/services/cookie.service'
import requestService from '@/services/request.service'
import { store } from '@/store/store'
import { CardListType, CardType, PagedData } from '@/types/card'
import { groupByModes, sortByModes } from '@/utils/constants'
import { cardListsToNames } from '@/utils/helpers'

import { SET_STATE } from '../actions/types'
import { CardListsStateType } from '../initialState'

import { filter, group, sort } from './updaters'

// Handle "Load More" button
const fetchMore = async (storeInitCardLists: CardListType[], moreIndex: number) => {
  const more = storeInitCardLists[moreIndex]?.more
  const res = await requestService.get(`${process.env.NEXT_PUBLIC_EDHREC_JSON_URL}/pages/${more}`, {}, true)
  const newPage = (await res.json()) as PagedData
  return newPage
}

const setState = (payload: Partial<CardListsStateType>) =>
  store.dispatch({
    payload,
    type: SET_STATE,
  })

type Args = Partial<CardListsStateType> & {
  moreIndex?: number
}

export const update = async (args: Args) => {
  const { cardlists, user } = store.getState()
  const { initCardLists: storeInitCardLists } = cardlists
  const persist = args.persist !== undefined ? args.persist : cardlists.persist
  const priceSource = user.priceSources[0] as keyof CardType['prices']
  const groupedBy = args.groupedBy || cardlists.groupedBy
  const sortedBy = args.sortedBy || cardlists.sortedBy
  const filters = args.filters || cardlists.filters

  // Abort in-progress requests and fetch card data
  requestService.abortAllRequests()

  // Check if we need to fetch full card data
  const groupBySkipFetch = groupByModes.find((mode) => mode.key === groupedBy)?.skipFetch
  const sortBySkipFetch = sortByModes.find((mode) => mode.key === sortedBy)?.skipFetch
  const needToFetch = !groupBySkipFetch || !sortBySkipFetch || filters.length > 0

  // Get card lists
  let cardMap: Record<string, CardType> = {}
  let initCardLists = args.initCardLists !== undefined ? args.initCardLists : storeInitCardLists
  if (args.moreIndex !== undefined) {
    setState({ loading: true })
    const newPage = await fetchMore(storeInitCardLists, args.moreIndex)
    initCardLists = [
      ...storeInitCardLists.slice(0, args.moreIndex),
      {
        ...storeInitCardLists[args.moreIndex],
        cardviews: [...storeInitCardLists[args.moreIndex].cardviews, ...newPage.cardviews],
        more: newPage.more,
      },
      ...storeInitCardLists.slice(args.moreIndex + 1),
    ]
    setState({ initCardLists })
    if (needToFetch) cardMap = await cardService.getCardMap(newPage.cardviews.map((card) => card.name))
  }

  // Fetch card data if needed
  if (args.moreIndex) {
    // Do nothing (loading state already set)
  } else if (needToFetch) {
    const payload = { loading: true } as Partial<CardListsStateType>
    if (args.groupedBy) payload.groupedBy = args.groupedBy
    if (args.sortedBy) payload.sortedBy = args.sortedBy
    if (args.filters) payload.filters = args.filters
    if (args.initCardLists !== undefined) payload.initCardLists = args.initCardLists
    setState(payload)
    cardMap = await cardService.getCardMap(cardListsToNames(initCardLists))
  } else if (args.initCardLists !== undefined) {
    setState({ initCardLists: args.initCardLists })
  }

  // Transform card lists
  const groupTransform = (cardLists: CardListType[]) =>
    group({
      cardLists,
      cardMap,
      groupedBy,
      initCardLists,
      priceSource,
    })
  const sortTransform = (cardLists: CardListType[]) =>
    sort({
      cardLists,
      cardMap,
      priceSource,
      sortedBy,
    })
  const filterTransform = (cardLists: CardListType[]) =>
    filter({
      cardLists,
      cardMap,
      filters,
      initCardLists,
      priceSource,
    })
  const loadMoreTransform = (cardLists: CardListType[]) => {
    if (initCardLists.length === 0) return cardLists
    if (initCardLists[0]?.more) cardLists[cardLists.length - 1].more = initCardLists[0].more
    return cardLists
  }
  const transforms = [groupTransform, sortTransform, filterTransform, loadMoreTransform]
  const newCardLists = transforms.reduce((acc, transform) => transform(acc), initCardLists)

  // Update cookies
  if (persist === true) {
    CookieService.set('groupedBy', groupedBy)
    CookieService.set('sortedBy', sortedBy)
    CookieService.setJSON('filters', filters)
  } else if (persist === false) {
    CookieService.remove('groupedBy')
    CookieService.remove('sortedBy')
    CookieService.remove('filters')
  }

  // Update store
  const payload = {
    cardLists: newCardLists,
    loading: false,
    updatedAt: Date.now(),
  } as Partial<CardListsStateType>
  if (args.groupedBy) payload.groupedBy = args.groupedBy
  if (args.sortedBy) payload.sortedBy = args.sortedBy
  if (args.filters) payload.filters = args.filters
  if (args.persist !== undefined) payload.persist = !cardlists.persist
  setState(payload)
}
